import { LineImageAlt } from '@qr-front/icons/line-image-alt'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { FileUploadToMediaStorage } from 'components/FileUploadToMediaStorage'
import { markContentTouched } from 'features/generator/generatorState'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useEffect, useImperativeHandle } from 'react'

import { TUploadedMedia } from '@/services'
import { getStore } from '@/store'

import {
	TContentFormHandle,
	TContentFormSectionPartial,
} from './qrContentFormTypes'

// 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024
const RESOLUTION_LIMIT = { width: 2048, height: 2048 }

export const imageFormAtom = atomWithReset<TUploadedMedia | null>(null)
export const imageFormFileAtom = atomWithReset<File | null>(null)

export const resetImageField = () => {
	getStore().set(imageFormAtom, RESET)
	getStore().set(imageFormFileAtom, RESET)
}

export const ImageField: TContentFormSectionPartial = forwardRef<
	TContentFormHandle,
	Parameters<TContentFormSectionPartial>[0] & {
		aspect?: number
		resizeResolution?: { width: number; height: number }
		showCropDialog?: boolean
		maintainAspectRatio?: boolean
	}
>(
	(
		{
			onValidChange,
			aspect = 1,
			resizeResolution,
			showCropDialog = true,
			maintainAspectRatio = false,
		},
		ref,
	) => {
		const t = useTranslations()

		const setImageFormAtom = useSetAtom(imageFormAtom)
		const [file, setFile] = useAtom(imageFormFileAtom)

		useEffect(() => {
			onValidChange(true)
		}, [onValidChange])

		useImperativeHandle(
			ref,
			() => ({
				validate: () => Promise.resolve({ isValid: true }),
			}),
			[],
		)

		const fileUploadTranslations = {
			title: t(
				'generator__content_form_section__about__personal_section__upload_image',
			),
			caption: t(
				'generator__content_form_section__about__personal_section__max_image_size',
				{ size: MAX_FILE_SIZE / 1024 / 1024 },
			),
			deleteButtonLabel: t('ui__file_upload__delete_button'),
			unsupportedFormatErrorMessage: t(
				'ui__file_upload__unsupported_format_error',
			),
			sizeTooLargeErrorMessage: t('ui__file_upload__file_size_error', {
				maxSize: MAX_FILE_SIZE / 1024 / 1024,
			}),
		}

		const cropDialogTranslations = {
			zoom: t('ui__image_crop_dialog__zoom'),
			closeButton: t('ui__popup__close_button'),
			saveButton: t('ui__image_crop_dialog__save_button'),
		}

		return (
			<div className="mt-6 desktop:mt-8">
				<label className={fieldLabelCls}>
					{t(
						'generator__content_form_section__business_page_screen__add_image',
					)}
				</label>
				<FileUploadToMediaStorage
					uploadPurpose="content_image"
					initialFileList={file ? [file] : []}
					onUploadStart={(incoming) => {
						markContentTouched()
						setFile(incoming)
					}}
					onUploadComplete={(
						uploaded: TUploadedMedia,
						original: File,
					) => {
						setImageFormAtom(uploaded)
						setFile(original)
					}}
					onDelete={() => {
						setImageFormAtom(null)
						setFile(null)
					}}
					maxFileSize={MAX_FILE_SIZE}
					placeholderIcon={<LineImageAlt />}
					translations={fileUploadTranslations}
					showCropDialog={showCropDialog}
					resizeResolution={resizeResolution || RESOLUTION_LIMIT}
					cropDialogTranslations={cropDialogTranslations}
					aspect={aspect}
					maintainAspectRatio={maintainAspectRatio}
					className="mt-2"
				/>
			</div>
		)
	},
)

ImageField.displayName = 'ImageField'
